/**
 * TruSpace Terms of Service — Version 1.3, effective August 22, 2026.
 *
 * Supersedes the Terms of Use last updated September 2020. When legal publishes
 * a new version, insert a new `agreements` row titled "Terms of Service" with a
 * newer `create_date` and update this component in the same change — never edit
 * an existing row. The forced-acceptance gate treats the latest row per title as
 * current (`server/src/features/auth/auth.legal.ts`).
 */

import {
  LegalDocumentHeader,
  LegalList,
  LegalMailto,
  LegalSection,
  LegalSubHeading,
} from '@/features/auth/components/LegalProse';

/** Full Terms of Service body for the auth dialogs and the acceptance gate. */
export function TermsOfServiceContent() {
  return (
    <div className="space-y-6 text-sm leading-relaxed text-muted-foreground">
      <LegalDocumentHeader
        title="TruSpace Terms of Service"
        version="Version 1.3"
        effectiveDate="August 22, 2026"
        supersedes="Supersedes the Terms of Use last updated September 2020"
      />

      <LegalSection id="tos-agreement" heading="1. AGREEMENT TO THESE TERMS">
        <p>
          These Terms of Service (“Terms”) are a binding agreement between you and
          Stevenson Systems, Inc. (“Stevenson Systems,” “SSI,” “we,” “us,” or “our”)
          governing your access to and use of the TruSpace platform at truspace.com
          and app.truspace.com, and any related applications, reports, and services
          (together, the “Services”).
        </p>
        <p>
          By creating an account, signing in, or otherwise using the Services, you
          agree to these Terms. If you do not agree, do not use the Services.
        </p>
        <p>
          If you are using the Services on behalf of an organization, you represent
          that the organization has authorized you to use the Services on its behalf.
        </p>
        <p>
          Where that organization has an executed Master Services Agreement with us,
          these Terms bind you as an individual. They do not amend, supplement, or
          vary that agreement, and your acceptance of these Terms is not an amendment
          of it. Where no Master Services Agreement is in place, you additionally
          represent that you have authority to bind the organization, and “you” means
          both you and that organization.
        </p>
      </LegalSection>

      <LegalSection
        id="tos-precedence"
        heading="2. RELATIONSHIP TO OTHER AGREEMENTS; ORDER OF PRECEDENCE"
      >
        <p>
          Most TruSpace access is purchased by an organization under a Master Services
          Agreement (“MSA”) and an Authorization Agreement. Individual people who use
          the platform — “Authorized Users” — are usually not signatories to those
          documents. These Terms bind those individuals directly.
        </p>
        <p>If there is a conflict, the following order controls, highest first:</p>
        <ol className="list-decimal space-y-2 pl-5">
          <li>
            The Master Services Agreement executed between Stevenson Systems and the
            client organization. Under MSA Section 19.6 its terms control any
            conflicting term in a referenced agreement or document.
          </li>
          <li>
            Any Authorization Agreement or Work Authorization executed under that
            Master Services Agreement.
          </li>
          <li>Any Data Processing Addendum executed between the parties.</li>
          <li>These Terms.</li>
          <li>The Privacy Notice, for matters concerning personal information.</li>
        </ol>
        <p>
          Nothing in these Terms reduces a client organization’s rights under an
          executed MSA. Where an MSA covers a subject these Terms also address — fees,
          liability, dispute resolution, confidentiality — the MSA governs the
          relationship between Stevenson Systems and that client organization, and
          these Terms govern the individual user’s conduct.
        </p>
        <p>
          These Terms are not an amendment to any MSA, and an Authorized User’s
          acceptance of them does not change the agreement between Stevenson Systems
          and that user’s organization. Under MSA Section 19.7 the MSA is the entire
          agreement between those parties.
        </p>
        <p>
          One qualification, stated plainly. MSA Section 10.2(vi) requires a client’s
          use of the TruSpace Platform to comply with the TruSpace Acceptable Use
          Policy published on our website, as updated periodically. Section 6 of these
          Terms is that Acceptable Use Policy. To that extent, and only to that extent,
          Section 6 reaches the client organization through the MSA itself rather than
          through these Terms.
        </p>
      </LegalSection>

      <LegalSection id="tos-definitions" heading="3. DEFINITIONS">
        <p>
          <span className="font-semibold text-foreground">“Authorized User”</span>{' '}
          means an individual permitted by a client organization to access the Services
          under that organization’s subscription.
        </p>
        <p>
          <span className="font-semibold text-foreground">“Client Property Data”</span>{' '}
          has the meaning given in MSA Section 1.5: information provided by Authorized
          Users to Stevenson Systems or uploaded to the TruSpace Platform, used in
          generating reports, performing calculations, and providing analytics about
          your organization’s properties and tenant spaces. It includes drawings,
          stacking plans, suite and lease records, tenant names, and occupancy details.
          Reports, calculations, and analyses we produce from it are TruSpace Materials,
          not Client Property Data.
        </p>
        <p>
          <span className="font-semibold text-foreground">“TruSpace Materials”</span>{' '}
          means the reports, data, analyses, software, and other materials we make
          available through the Services, including Calculation Analysis Reports,
          Certified Measurement Reports, and Space Accounting Reports.
        </p>
        <p>
          Capitalized terms not defined here have the meaning given in the MSA.
        </p>
      </LegalSection>

      <LegalSection id="tos-eligibility" heading="4. ELIGIBILITY AND ACCOUNTS">
        <p>
          You must be at least 18 years old and capable of forming a binding contract.
          The Services are for business use only and are not intended for personal,
          family, or household purposes.
        </p>
        <p>
          You must provide accurate registration information and keep it current. You
          are responsible for all activity under your account and for keeping your
          credentials confidential. Do not share your account. Tell us promptly at{' '}
          <LegalMailto email="support@truspace.com" /> if you suspect unauthorized use.
        </p>
        <p>
          Your organization’s administrator may add, suspend, or remove Authorized
          Users, and may see your account details and activity in the platform. Your
          right to use the Services depends on your organization maintaining an active
          subscription and on your continued authorization by that organization.
        </p>
      </LegalSection>

      <LegalSection id="tos-license" heading="5. ACCESS AND LICENSE">
        <p>
          Subject to these Terms and your organization’s subscription, we grant you a
          limited, non-exclusive, non-transferable right to access and use the Services
          and TruSpace Materials for your organization’s internal business purposes.
          Your individual access may be suspended or removed under Section 13. That is
          personal to you and does not affect your organization’s license for the
          Subscription Term under MSA Section 10.2.
        </p>
        <p>
          You may download or print TruSpace Materials for that internal use. All other
          rights are reserved. You may not resell, sublicense, distribute, or make the
          Services or TruSpace Materials available to any third party except as the MSA
          expressly permits.
        </p>
      </LegalSection>

      <LegalSection
        id="tos-acceptable-use"
        heading="6. ACCEPTABLE USE AND PROHIBITED ACTIVITIES"
      >
        <p>
          This Section is the TruSpace Acceptable Use Policy referred to in MSA Section
          10.2(vi).
        </p>
        <p>
          You may use the Services only for lawful business purposes and only as
          permitted by these Terms. You agree not to do any of the following, and not
          to help anyone else do them:
        </p>
        <LegalList>
          <li>
            Access, use, or copy the Services or TruSpace Materials for any purpose
            other than your organization’s internal business use. MSA Section 10.4
            requires our prior written permission before the Services or TruSpace
            Materials are made available to a third party. Separately, under MSA
            Section 8.3 your organization may instruct us to share information with its
            engineers, architects, brokers, or others it designates, and we will act on
            that instruction.
          </li>
          <li>
            Scrape, harvest, index, or systematically extract data from the Services,
            whether manually or using any bot, spider, crawler, or automated process,
            except through interfaces we expressly provide for that purpose.
          </li>
          <li>
            Reverse engineer, decompile, disassemble, or attempt to derive the source
            code, structure, or underlying ideas of the Services, or reproduce any part
            of their functionality in a competing product.
          </li>
          <li>
            Use the Services, TruSpace Materials, or any measurement methodology to
            build, train, benchmark, or improve a competing product or service.
          </li>
          <li>
            Circumvent, disable, or interfere with security, authentication,
            rate-limiting, or access-control features, or probe or scan the Services for
            vulnerabilities without our prior written permission.
          </li>
          <li>
            Access any account, data, building record, or portion of the Services you
            are not authorized to access, including data belonging to another client
            organization.
          </li>
          <li>
            Upload or transmit any virus, worm, malicious code, or anything designed to
            disrupt, damage, or gain unauthorized access to the Services or to any
            system or data.
          </li>
          <li>
            Interfere with or place an unreasonable load on the Services or the
            infrastructure that supports them, including denial-of-service activity and
            excessive automated requests.
          </li>
          <li>
            Remove, obscure, or alter any copyright, trademark, or other proprietary
            notice in the Services or TruSpace Materials.
          </li>
          <li>
            Misrepresent a Non-Certified Measurement Report, draft report, or
            AI-generated output as a Certified Measurement Report, or otherwise misstate
            the status, scope, or provenance of any TruSpace Material.
          </li>
          <li>
            Upload any data you do not have the right to provide, or any personal
            information beyond what is necessary for the Services.
          </li>
          <li>
            Upload sensitive personal information — including government identification
            numbers, financial account numbers, health information, or biometric data —
            which the Services are not designed to hold.
          </li>
          <li>
            Impersonate any person or entity, or misrepresent your affiliation with any
            person or entity.
          </li>
          <li>
            Use the Services to send unsolicited commercial messages, or to store or
            transmit material that is unlawful, defamatory, harassing, or infringing.
          </li>
          <li>
            Violate any applicable law or regulation, including data protection, export
            control, and sanctions laws.
          </li>
          <li>
            Share, sell, or transfer your account credentials, or permit anyone who is
            not an Authorized User to use the Services through your account.
          </li>
        </LegalList>
        <p>
          We may investigate suspected violations and may suspend or terminate access
          under Section 13. Serious violations may be referred to law enforcement.
        </p>
      </LegalSection>

      <LegalSection id="tos-ownership" heading="7. OWNERSHIP OF DATA AND MATERIALS">
        <LegalSubHeading>What your organization owns</LegalSubHeading>
        <p>
          Client Property Data — the drawings, stacking plans, suite and lease records,
          and occupancy information your organization supplies — remains your
          organization’s property. We use it only to provide the Services, and we
          acquire no right, title, or interest in it beyond what is necessary to do so.
        </p>
        <p>
          Under MSA Section 10.5, your organization grants us a non-exclusive right to
          use, copy, distribute, and display Client Property Data solely in connection
          with the operation of the TruSpace Platform on behalf of your organization.
          These Terms restate that grant and neither widen nor narrow it. Hosting,
          storage, transmission, and display by the service providers to whom we send
          Client Property Data, identified in our Privacy Notice, are carried out within
          that grant and as contractor disclosures permitted by MSA Section 8.3.
        </p>
        <p>
          We do not use Client Property Data for any purpose outside operating the
          Services for your organization. Where an MSA or Data Processing Addendum
          defines this right differently, that definition controls.
        </p>
        <p>
          Your organization represents that it has the rights and permissions necessary
          to provide the Client Property Data submitted through its account, and that
          our use of it as described will not infringe anyone’s rights or violate any
          law or lease obligation. Your organization is responsible for the accuracy,
          integrity, and reliability of the data it supplies. That representation is
          your organization’s and not yours personally, and Section 16 does not make you
          individually answerable for it.
        </p>

        <LegalSubHeading>What Stevenson Systems owns</LegalSubHeading>
        <p>
          We retain exclusive ownership of everything we produce and of the platform
          itself. That includes all Calculation Analysis Reports, Certified Measurement
          Reports, Space Accounting Reports, and other TruSpace Materials, together with
          the TruSpace Platform, DrawingVault, StackTower, and TruDocs, and all systems,
          documentation, forms, graphics, improvements, enhancements, and derivative
          works relating to them. These are protected by copyright and other
          intellectual property rights and remain the sole property of Stevenson
          Systems.
        </p>
        <p>
          Your organization receives a license to use TruSpace Materials for its
          internal business purposes under its MSA and Section 5 of these Terms. It
          acquires no ownership interest in them. Delivery of a report does not transfer
          ownership of that report.
        </p>

        <LegalSubHeading>Property images</LegalSubHeading>
        <p>
          Under MSA Section 10.6, for the Properties listed on your organization’s
          Authorization Agreement, your organization grants us the irrevocable and
          perpetual right to create and use images depicting the Property in any media
          for any purpose except defamation or pornography, which may include
          advertising, promotion, and marketing, worldwide and without further consent.
          All interest in those images, including copyright, belongs exclusively to
          Stevenson Systems. Your organization waives any right to inspect or approve
          those images or their use, and this grant binds future owners, successors, and
          assigns of the Property.
        </p>

        <LegalSubHeading>Aggregated statistics</LegalSubHeading>
        <p>
          The line we draw is whose information it is, because MSA Section 8.2 restricts
          each party’s use of the other party’s Confidential Information. Origin
          governs, not subject matter. Under MSA Section 10.1.2 we own the reports,
          drawings, and calculations we produce and deliver, and the internal working
          records we generate in producing them are our own information too. We may
          aggregate that material and use it within Stevenson Systems to run, support,
          secure, and improve the Services.
        </p>
        <p>
          Two categories are your organization’s rather than ours, and we aggregate
          neither for separate purposes. The first is Client Property Data. The second
          is platform usage telemetry: MSA Section 8.1 lists TruSpace usage statistics
          as Confidential Information, and that data records your organization’s
          activity rather than our work, so we use it to operate, monitor, secure, and
          repair the Services and for nothing else.
        </p>
        <p>
          We do not derive statistics, benchmarks, or comparisons from Client Property
          Data — the information your organization provides to us or uploads to the
          platform, including tenant names, lease commencement and termination dates,
          and occupancy details. Under MSA Section 10.5 our license in that data extends
          only to operating the TruSpace Platform on your organization’s behalf, and MSA
          Section 8.2 limits our use of your organization’s Confidential Information to
          what is necessary to perform the Agreement.
        </p>
        <p>
          We do not publish or otherwise disclose these aggregates outside Stevenson
          Systems, and we will not attempt to re-identify aggregated information. If
          that ever changes, we will update these Terms before it does, and no aggregate
          will identify your organization, a building, a tenant, or an individual user.
        </p>
      </LegalSection>

      <LegalSection id="tos-ai" heading="8. ARTIFICIAL INTELLIGENCE FEATURES">
        <p>
          The Services include features powered by artificial intelligence, including
          large language models operated by third parties (“AI Features”). TruInsight is
          one such feature. By using AI Features, you acknowledge and agree to the
          following.
        </p>
        <LegalList>
          <li>
            AI Features may produce output that is inaccurate, incomplete, or unsuitable
            for your purpose. You are solely responsible for reviewing and verifying any
            AI-generated output before relying on it.
          </li>
          <li>
            AI-generated output is informational and assistive only. It is not a
            Certified Measurement Report, is not field-verified, and must not be
            represented as either. Do not rely on it for business, safety, legal, or
            compliance-critical decisions without independent verification.
          </li>
          <li>
            To generate output, certain inputs are transmitted to a third-party AI model
            provider. For TruInsight this includes your question together with a limited
            preview of matching property records. The current provider is identified in
            our Privacy Notice and sub-processor list. Our arrangements with that
            provider restrict its use of the information to performing the service for
            us, as MSA Section 8.3 requires.
          </li>
          <li>
            That transmission is a disclosure to a contractor under MSA Section 8.3,
            which permits disclosure to contractors who need the information, have
            agreed in writing to keep it confidential, and may use it only to facilitate
            their work for Stevenson Systems under the Agreement. It is not a grant of
            any new right over Client Property Data.
          </li>
          <li>
            AI Features are provided “AS IS” and without warranty of any kind. To the
            fullest extent permitted by law, we disclaim all liability arising from your
            use of or reliance on AI-generated output.
          </li>
          <li>
            An organization administrator may ask us to disable AI Features for their
            account by writing to <LegalMailto email="support@truspace.com" />. Disabling
            AI Features may remove functionality from the Services.
          </li>
          <li>
            You will not use AI Features to generate content that violates Section 6,
            nor attempt to extract, reverse engineer, replicate, or misuse the underlying
            models, prompts, or system instructions — including through prompt injection
            or similar techniques.
          </li>
          <li>
            You will not submit to AI Features any data you are not permitted to disclose
            to a third-party processor under your own confidentiality or lease
            obligations.
          </li>
        </LegalList>
      </LegalSection>

      <LegalSection id="tos-ip" heading="9. INTELLECTUAL PROPERTY">
        <p>
          The Services, TruSpace Materials, and all software, databases, designs, text,
          graphics, and other content we provide, together with all trademarks, service
          marks, and logos, are owned by or licensed to Stevenson Systems and are
          protected by intellectual property law. Except for the limited rights granted
          in Section 5, nothing in these Terms transfers any right in them to you.
        </p>
        <p>
          If you send us suggestions, feedback, or ideas about the Services, we may use
          them for any purpose without obligation or compensation to you, and you retain
          no rights in the resulting improvements to the Services. This does not apply
          to your organization’s proprietary materials or pre-existing intellectual
          property, or to anything containing its Confidential Information. MSA Sections
          8 and 10.1.1 govern those, and under Section 10.1.1 Stevenson Systems takes no
          right, title, or interest in materials your organization provides beyond what
          is necessary to perform the Services.
        </p>
      </LegalSection>

      <LegalSection id="tos-third-party" heading="10. THIRD-PARTY SERVICES AND CONTENT">
        <p>
          The Services may integrate with or link to third-party products, content, or
          services. We do not control them, do not endorse them, and are not responsible
          for them. Your use of a third-party service is governed by that provider’s
          terms. If a third-party provider changes, restricts, or discontinues its
          offering, we will use commercially reasonable efforts to find an alternative,
          but we are not liable for the interruption.
        </p>
        <p>
          We publish a current list of the third-party providers that process data on
          our behalf — including hosting, identity, content delivery, support, mapping,
          and AI model providers — as part of our Privacy Notice and sub-processor list.
          We will give advance notice before adding a provider that handles personal
          information.
        </p>
        <p>
          Where we send those providers Client Property Data, they receive it as
          contractors under MSA Section 8.3 and not under any separate right granted by
          these Terms. Mapping and address-lookup services are different: your browser
          requests map tiles and address lookups directly, so we do not send them Client
          Property Data and they are not contractor disclosures. Our Privacy Notice
          marks which providers fall into which category. MSA Section 10.3 records your
          organization’s acknowledgement that third-party content and services —
          including cloud storage providers and analytics tools — may be integrated with
          or necessary for the Services.
        </p>
        <p>
          Mapping features in the Services use the Leaflet library with OpenStreetMap
          data and base map tiles served by CARTO. OpenStreetMap data is made available
          under the Open Database License. You must not remove, obscure, or alter the map
          attribution displayed in the Services, and you may not extract or redistribute
          the underlying map data except as that license permits.
        </p>
      </LegalSection>

      <LegalSection id="tos-confidentiality" heading="11. CONFIDENTIALITY">
        <p>
          The Services and TruSpace Materials contain confidential and proprietary
          information of Stevenson Systems and, in some cases, of other parties. Do not
          disclose them outside your organization except as the MSA permits. Where an
          MSA governs confidentiality between Stevenson Systems and your organization,
          that section controls.
        </p>
      </LegalSection>

      <LegalSection id="tos-fees" heading="12. FEES">
        <p>
          Fees, invoicing, payment terms, renewals, and refunds are governed by the MSA
          and the applicable Authorization Agreement, not by these Terms. If you access
          the Services as an Authorized User under your organization’s subscription, you
          personally owe no fees to us under these Terms.
        </p>
      </LegalSection>

      <LegalSection
        id="tos-termination"
        heading="13. TERM, TERMINATION, AND ACCOUNT REMOVAL"
      >
        <p>
          These Terms apply for as long as you use the Services. The commercial term of
          your organization’s subscription is governed by its MSA and Authorization
          Agreement; this Section describes what happens to access and data.
        </p>

        <LegalSubHeading>Removing an individual account</LegalSubHeading>
        <p>
          Your organization’s administrator may remove your account at any time, and
          should do so when you change roles or leave the organization. You may also ask
          us to close your account by writing to{' '}
          <LegalMailto email="support@truspace.com" />; we will confirm the request with
          your organization’s administrator before acting, because the underlying
          subscription and data belong to that organization.
        </p>
        <p>
          When an individual account is removed, that person loses access immediately.
          Client Property Data, reports, and account activity records belonging to the
          organization are unaffected and remain in the organization’s account.
        </p>

        <LegalSubHeading>Suspension</LegalSubHeading>
        <p>
          We may suspend your access, with notice where practicable, if you violate
          these Terms, if your organization’s account is delinquent, if your organization
          revokes your authorization, or if we reasonably believe suspension is necessary
          to protect the Services, our clients, or any person. We will restore access
          once the cause is resolved. This describes how we handle individual accounts
          and does not limit our rights under MSA Sections 7.9 and 9.3.
        </p>

        <LegalSubHeading>Termination of the subscription</LegalSubHeading>
        <p>
          Either party may terminate for convenience on thirty (30) days’ prior written
          notice, and either party may terminate immediately for material breach or
          insolvency, in each case as set out in the MSA. MSA Section 9.1 provides that
          such termination is “without penalty or more Fees beyond those specified
          herein.” MSA Section 9.4 provides that where a client terminates a TruSpace
          Subscription before the end of the Subscription Term, the client pays the Fees
          owed in full for the remainder of that term. Those two provisions sit in
          tension and neither these Terms nor Stevenson Systems resolves that tension in
          its own favor; as between Stevenson Systems and a client organization, the MSA
          governs and both Sections apply according to their terms.
        </p>

        <LegalSubHeading>Inactivity and abandonment</LegalSubHeading>
        <p>
          The MSA permits us to treat an account as abandoned and terminate access if an
          invoice remains unpaid for more than ninety (90) days, if delivered reports sit
          unapproved as drafts for six (6) months without response, if there is no
          activity on the account for six (6) months, or if services are deferred for
          more than six (6) months. We will attempt to reach the organization’s
          administrator before acting on inactivity, though MSA Section 9.3 does not
          require us to.
        </p>

        <LegalSubHeading>Getting your data back</LegalSubHeading>
        <p>
          Under MSA Section 9.4, on our receipt of all Fees owed for the remainder of
          the Subscription Term, Client Property Data remains available for download
          through the termination date and for a thirty (30) day grace period afterwards.
          After that period we may remove it from the TruSpace production environment and
          the MSA grants no further right of retrieval. We may still be able to help
          after the window closes, but that is at our discretion and should not be relied
          on. Retrieve anything you need within the window — plan for it before you
          terminate, not after.
        </p>

        <LegalSubHeading>Non-renewal</LegalSubHeading>
        <p>
          If your organization chooses not to renew, access is not deleted. It is reduced
          to the TruSpace Base Limited-Service Level, and per-request fees then apply for
          information requested through TruSpace Orders.
        </p>

        <LegalSubHeading>What survives</LegalSubHeading>
        <p>
          Sections 6, 7, 9, 11, and 14 through 20 survive termination, along with any
          obligation in the MSA that is intended to survive — including confidentiality,
          protection of intellectual property, indemnification, and payment of
          outstanding Fees. The ownership positions in Section 7, including our rights in
          TruSpace Materials and property images, survive termination in full.
        </p>
      </LegalSection>

      <LegalSection id="tos-disclaimers" heading="14. DISCLAIMERS">
        <p>
          THE SERVICES AND TRUSPACE MATERIALS ARE PROVIDED “AS IS” AND “AS AVAILABLE.”
          TO THE FULLEST EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR
          IMPLIED, INCLUDING THE IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
          PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT.
        </p>
        <p>
          WE DO NOT WARRANT THAT THE SERVICES WILL BE UNINTERRUPTED, TIMELY, SECURE, OR
          ERROR-FREE, OR THAT ANY OUTPUT WILL BE ACCURATE OR COMPLETE.
        </p>
        <p>
          This Section does not limit any express written warranty or commitment
          Stevenson Systems gives a client organization in the MSA. Those include the
          thirty (30) day limited warranty on professional services in MSA Section 13,
          and the commitments in MSA Section 12 that information delivered through
          TruSpace will be complete and unmodified and that calculations will adhere to
          ANSI/BOMA variance guidelines with a tolerance of two percent (2%). Where those
          apply, they control for that client organization and this Section does not
          disclaim them.
        </p>
      </LegalSection>

      <LegalSection id="tos-liability" heading="15. LIMITATION OF LIABILITY">
        <p>
          TO THE FULLEST EXTENT PERMITTED BY LAW, NEITHER STEVENSON SYSTEMS NOR ITS
          OFFICERS, DIRECTORS, EMPLOYEES, OR AGENTS WILL BE LIABLE FOR ANY INDIRECT,
          CONSEQUENTIAL, SPECIAL, INCIDENTAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR FOR ANY
          LOSS OF PROFITS, REVENUE, DATA, OR BUSINESS OPPORTUNITY, EVEN IF ADVISED OF THE
          POSSIBILITY.
        </p>
        <p>
          OUR TOTAL AGGREGATE LIABILITY ARISING OUT OF OR RELATING TO THESE TERMS OR THE
          SERVICES WILL NOT EXCEED THE GREATER OF (A) THE TOTAL FEES PAID TO US FOR THE
          SERVICES DURING THE TWELVE (12) MONTHS IMMEDIATELY PRECEDING THE EVENT GIVING
          RISE TO THE CLAIM, OR (B) ONE HUNDRED U.S. DOLLARS ($100).
        </p>
        <p>
          These limits do not apply to liability that cannot be limited by law, or to
          liability arising from our gross negligence, willful misconduct, or fraud, or
          for death or personal injury caused by our negligence.
        </p>
        <p>
          For a client organization with an executed MSA, the limitation of liability in
          MSA Section 11 controls in full, including where it produces a lower cap than
          this Section would. This Section applies only to claims by an individual
          Authorized User that MSA Section 11 does not address, and the floor in (B)
          exists because an Authorized User pays us nothing, so a fees-only cap would
          compute to zero.
        </p>
        <p>
          Some jurisdictions do not allow certain limitations, so some of the above may
          not apply to you.
        </p>
      </LegalSection>

      <LegalSection id="tos-indemnification" heading="16. INDEMNIFICATION">
        <p>
          You will defend, indemnify, and hold harmless Stevenson Systems and its
          officers, directors, employees, and agents from any third-party claim, loss,
          liability, or expense, including reasonable attorneys’ fees, arising out of:
          (a) your use of the Services outside the scope of these Terms; (b) your breach
          of these Terms; or (c) your violation of any law or of any third party’s rights.
        </p>
        <p>
          This Section does not make you personally responsible for your organization’s
          data. Where you access the Services as an Authorized User under your
          organization’s MSA, claims concerning Client Property Data are a matter between
          Stevenson Systems and your organization, and this Section does not reach them.
          MSA Section 14 allocates third-party intellectual property claims between those
          parties mutually and MSA Section 14.1 covers the client’s violation of law; a
          claim of any other kind is allocated by the MSA as a whole rather than by this
          Section.
        </p>
        <p>
          We will notify you of any claim subject to this Section, and you may control
          the defense with counsel reasonably acceptable to us. Neither party will settle
          a claim in a way that imposes an obligation on the other or admits fault on its
          behalf without that party’s written consent, which will not be unreasonably
          withheld.
        </p>
        <p>
          Where an MSA contains mutual indemnification between Stevenson Systems and a
          client organization, that provision controls as between those parties,
          including the remedies in MSA Section 14.3 — procuring the right to continue
          using the Services, modifying them to be non-infringing, or terminating and
          giving a prorated refund of prepaid Fees for the unused portion.
        </p>
      </LegalSection>

      <LegalSection
        id="tos-disputes"
        heading="17. DISPUTE RESOLUTION; BINDING ARBITRATION"
      >
        <p className="font-semibold text-foreground">
          Please read this Section carefully. It requires arbitration of most disputes
          and waives your right to a jury trial and to participate in a class action.
        </p>

        <LegalSubHeading>Informal resolution first</LegalSubHeading>
        <p>
          Before starting arbitration, the parties will attempt to resolve the dispute
          through good-faith negotiation for thirty (30) days after written notice, and
          then through mediation. Send notice to{' '}
          <LegalMailto email="info@truspace.com" /> and to the address in Section 21.
        </p>

        <LegalSubHeading>Binding arbitration</LegalSubHeading>
        <p>
          If the dispute is not resolved through negotiation and mediation, either party
          may submit it to binding arbitration administered by the American Arbitration
          Association under its Commercial Arbitration Rules. The arbitration will be
          conducted by one (1) arbitrator selected by mutual agreement; if the parties
          cannot agree, the AAA will appoint the arbitrator under its rules. The seat and
          place of arbitration will be Orange County, California. Proceedings will be
          conducted in English. The arbitrator’s award is final and binding, and judgment
          may be entered in any court of competent jurisdiction.
        </p>
        <p>
          This uses the same forum, seat, and rules as MSA Section 17, so a client
          organization and its Authorized Users are routed to the same place rather than
          two different ones. It is not identical to the MSA procedure. These Terms add a
          class action waiver, a thirty (30) day arbitration opt-out, and court exceptions
          for small claims and injunctive relief, none of which appear in MSA Section 17.
          MSA Section 17.1.1 requires mediation within seven (7) additional days after
          negotiation fails, and MSA Section 17.1.2 provides that each party bears its own
          arbitration costs and attorneys’ fees with administrative and arbitrator fees
          divided equally. MSA Section 17.1.1 also starts its thirty-day clock when the
          dispute arises rather than on written notice, and MSA Section 17.1.2 lets the
          arbitrator reallocate fees where an equal division would be unjust. The
          limitation period differs too, as set out below. For a client organization, MSA
          Section 17 controls.
        </p>

        <LegalSubHeading>Class action waiver</LegalSubHeading>
        <p>
          Arbitration is limited to the dispute between you and Stevenson Systems
          individually. To the fullest extent permitted by law: no arbitration may be
          joined with any other proceeding; there is no right to arbitrate on a class or
          collective basis; and no dispute may be brought in a representative capacity on
          behalf of the general public or any other person.
        </p>

        <LegalSubHeading>Exceptions</LegalSubHeading>
        <p>
          Either party may bring an individual action in small claims court, and either
          party may seek injunctive or other equitable relief in the state or federal
          courts located in Orange County, California, for: (a) claims concerning
          intellectual property rights; (b) claims arising from theft, piracy,
          unauthorized access, or invasion of privacy; and (c) any claim for injunctive
          relief.
        </p>

        <LegalSubHeading>Your right to opt out of arbitration</LegalSubHeading>
        <p>
          You may opt out of this arbitration agreement by writing to{' '}
          <LegalMailto email="info@truspace.com" /> within thirty (30) days of first
          accepting these Terms, stating your name and that you are opting out of
          arbitration. Opting out does not affect any other part of these Terms, and we
          will not treat it as a reason to refuse or restrict your access to the Services.
        </p>

        <LegalSubHeading>Time limit for claims</LegalSubHeading>
        <p>
          Any claim arising out of or relating to these Terms must be filed within one
          (1) year after it arose, or it is permanently barred, except where a shorter or
          longer period is required by law.
        </p>
        <p>
          For a client organization with an executed MSA, the six (6) month period in MSA
          Section 17.1.3 controls any claim arising out of or relating to that agreement.
          The one-year period above applies to claims by an individual Authorized User
          under these Terms.
        </p>
      </LegalSection>

      <LegalSection id="tos-governing-law" heading="18. GOVERNING LAW AND VENUE">
        <p>
          These Terms are governed by the laws of the State of California, without regard
          to its conflict of laws principles. Subject to Section 17, the parties submit to
          the exclusive jurisdiction of the state and federal courts located in Orange
          County, California.
        </p>
      </LegalSection>

      <LegalSection id="tos-changes" heading="19. CHANGES TO THESE TERMS">
        <p>
          We may update these Terms from time to time. The updated version is effective
          when we post it. If we make a material change, we will try to notify you by
          email or in the product. Continued use of TruSpace after the update is
          acceptance of the new Terms.
        </p>
        <p>
          Because Section 6 is the Acceptable Use Policy referred to in MSA Section
          10.2(vi), a change to Section 6 also takes effect for client organizations under
          that provision. We will give client organizations at least thirty (30) days’
          notice before a change to Section 6 that materially expands what it prohibits.
        </p>
        <p>We keep prior versions available so you can see what changed and when.</p>
      </LegalSection>

      <LegalSection id="tos-general" heading="20. GENERAL PROVISIONS">
        <p>
          <span className="font-semibold text-foreground">Entire agreement.</span> These
          Terms, together with the documents listed in Section 2, are the entire agreement
          between you as an individual user and us regarding your use of the Services, and
          supersede the Terms of Use dated September 2020. As between Stevenson Systems
          and a client organization, the Master Services Agreement is the entire agreement
          under its Section 19.7, and these Terms do not alter it.
        </p>
        <p>
          <span className="font-semibold text-foreground">Assignment.</span> You may not
          assign these Terms without our written consent. We may assign these Terms in
          connection with a merger, acquisition, or sale of assets. Assignment of a client
          organization’s Master Services Agreement is a separate matter: MSA Section 19
          permits no assignment by either party without the other party’s written consent,
          and nothing here changes that.
        </p>
        <p>
          <span className="font-semibold text-foreground">Severability.</span> If any
          provision is held unenforceable, it will be limited or removed to the minimum
          extent necessary and the rest remains in effect.
        </p>
        <p>
          <span className="font-semibold text-foreground">No waiver.</span> Our failure to
          enforce any provision is not a waiver of it.
        </p>
        <p>
          <span className="font-semibold text-foreground">Force majeure.</span> Neither
          party is liable for a failure to perform caused by events beyond its reasonable
          control. For a client organization, MSA Section 16 also applies: where the event
          continues for more than thirty (30) days either party may terminate on written
          notice, with no liability other than a prorated refund of Fees for Services not
          rendered.
        </p>
        <p>
          <span className="font-semibold text-foreground">No agency.</span> These Terms
          create no partnership, joint venture, employment, or agency relationship.
        </p>
        <p>
          <span className="font-semibold text-foreground">
            Electronic communications.
          </span>{' '}
          You consent to receive communications from us electronically, and agree that
          electronic notices, agreements, and records satisfy any legal requirement that
          they be in writing.
        </p>
        <p>
          <span className="font-semibold text-foreground">Export and sanctions.</span> You
          represent that you are not located in, and will not use the Services on behalf
          of anyone located in, a jurisdiction subject to comprehensive U.S. sanctions,
          and that you are not on any U.S. restricted-party list.
        </p>
        <p>
          <span className="font-semibold text-foreground">Interpretation.</span> These
          Terms will not be construed against us solely because we drafted them.
        </p>
      </LegalSection>

      <LegalSection id="tos-contact" heading="21. CONTACT US">
        <p>
          Questions about these Terms, or notices required under them:
        </p>
        <p>
          Email: <LegalMailto email="info@truspace.com" />
          <br />
          Support: <LegalMailto email="support@truspace.com" />
          <br />
          Post: Stevenson Systems, Inc., 27822 El Lazo Road, Laguna Niguel, CA 92677,
          United States
          <br />
          Phone: 949-297-4200
        </p>
      </LegalSection>
    </div>
  );
}
